import React, { useState, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl } from "../../../comman/config";
import { can } from "../../../comman/permissions";
import { useNavigate, Link } from 'react-router-dom';
import { menuAboveTable } from "../../../comman/selectMenu";

// Role Master: the roles a user can hold, and the operations each one is allowed.
//
// The SuperAdmin row is read-only. It always holds every operation and is always active, whatever
// the row says -- see backend/utils/roles.js -- because it is the account that fixes a permissions
// mistake, and it must not be possible to make that mistake on it.
const SUPERADMIN_ROLE = 0;

function List() {

    const [roleList, setRoleList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [operationOptions, setOperationOptions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    // Status defaults to Active, matching the other master lists. '' is All.
    const [statusFilter, setStatusFilter] = useState({ value: '1', label: 'Active' });
    const [operationFilter, setOperationFilter] = useState([]);

    const role = String(localStorage.getItem('userRole'));
    const canDisable = can('disable');
    const canActivate = can('active');
    const canDelete = can('delete');

    const navigate = useNavigate();

    useEffect(() => {
        getRoleList();
        getOperations();
    }, []);

    useEffect(() => {
        const query = searchQuery.trim().toLowerCase();
        const status = statusFilter?.value ?? '';
        const wanted = operationFilter.map((operation) => operation.value);
        setFilteredList(roleList.filter((item) => {
            const operations = String(item.operations ?? '').split(',').filter((entry) => entry !== '');
            const matchesQuery = !query || (item.name ?? '').toLowerCase().includes(query);
            // The superadmin is always active whatever the column says, so it belongs under the
            // Active filter rather than dropping out of the default view.
            const effectiveStatus = item.id === SUPERADMIN_ROLE ? '1' : String(item.status);
            const matchesStatus = status === '' || effectiveStatus === status;
            const matchesOperations = wanted.length === 0
                || wanted.some((operation) => operations.includes(operation));
            return matchesQuery && matchesStatus && matchesOperations;
        }));
    }, [searchQuery, statusFilter, operationFilter, roleList]);

    const getRoleList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/role_master/list`);
            setRoleList(response.data.data);
        } catch (error) {
            console.log(error);
        }
    }

    const getOperations = async () => {
        try {
            const response = await axios.get(`${baseUrl}/role_master/operations`);
            setOperationOptions(response.data?.data ?? []);
        } catch (error) {
            console.log(error);
        }
    }

    const handelFilterChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleClear = () => {
        setSearchQuery('');
        setStatusFilter({ value: '1', label: 'Active' });
        setOperationFilter([]);
    };

    // The stored CSV, spelled out with the labels the form uses.
    const operationsLabel = (operations) => {
        const list = String(operations ?? '').split(',').filter((entry) => entry !== '');
        if (list.length === 0) return '-';
        return list
            .map((entry) => operationOptions.find((option) => option.value === entry)?.label ?? entry)
            .join(', ');
    };

    const deleteRole = async (id) => {
        if (!window.confirm("Are you sure you want to disable this role? Its users keep the role but lose every operation.")) return;
        try {
            await axios.delete(`${baseUrl}/role_master/delete/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not disable the role.');
        }
    };

    const enableRole = async (id) => {
        if (!window.confirm("Enable this role?")) return;
        try {
            await axios.post(`${baseUrl}/role_master/enable/${id}`, { role });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not enable the role.');
        }
    };

    // Delete removes the row, unlike Disable which is recoverable, so its confirmation says so.
    // Whether any account still holds the role is the backend's call -- it counts and refuses.
    const destroyRole = async (id) => {
        if (!window.confirm("Permanently DELETE this role? Disable is reversible; this is not.")) return;
        try {
            await axios.delete(`${baseUrl}/role_master/destroy/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not delete the role.');
        }
    };

    const editRole = async (id) => {
        navigate(`/role_master/edit/${btoa(id)}`, { state: roleList.filter(item => item.id == id) });
    }

    return (
        <div className="p-4">
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Role</h1>
                <div>
                    <Link to="/role_master/add">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add Role</button>
                    </Link>
                </div>
            </div>
            <div className="relative shadow-md sm:rounded-lg">
                <div className="pb-4 flex flex-column sm:flex-row flex-wrap space-y-4 sm:space-y-0 items-center justify-between">
                    <label htmlFor="table-search" className="sr-only">Search</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 rtl:inset-r-0 rtl:right-0 flex items-center ps-3 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path></svg>
                        </div>
                        <input type="text" value={searchQuery} onChange={handelFilterChange} id="table-search" className="block p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Search for roles" />
                    </div>
                    <div className="flex items-center gap-4 ms-4 me-auto">
                        <Select
                            {...menuAboveTable}
                            isMulti
                            classNamePrefix="select"
                            className="basic-multi-select min-w-64"
                            value={operationFilter}
                            name="operations"
                            onChange={(selected) => setOperationFilter(selected ?? [])}
                            options={operationOptions}
                            placeholder="Operation"
                        />
                        <Select
                            {...menuAboveTable}
                            classNamePrefix="select"
                            className="basic-multi-select w-36"
                            value={statusFilter}
                            name="status"
                            onChange={(selected) => setStatusFilter(selected)}
                            options={[
                                { value: '', label: 'All' },
                                { value: '1', label: 'Active' },
                                { value: '0', label: 'Inactive' }
                            ]}
                            placeholder="Status"
                            isClearable={false}
                        />
                        <button
                            onClick={handleClear}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                        >
                            Clear
                        </button>
                    </div>
                </div>
                {/* The header stays put while the rows scroll under it. The height cap is what
                    makes that work -- a container with no limit never scrolls, and a sticky
                    header has nothing to hold itself against. */}
                <div className="overflow-auto max-h-[65vh]">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Role Name</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Operation</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Users</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Status</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.map((item) => {
                            const isSuperadminRole = item.id === SUPERADMIN_ROLE;
                            return (
                            <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{item.name}</th>
                                <td className="px-6 py-4">{operationsLabel(item.operations)}</td>
                                <td className="px-6 py-4">{item.users}</td>
                                <td className="px-6 py-4">
                                    {item.status == 1 ? 'Active' : 'Inactive'}
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex gap-2">
                                        {isSuperadminRole
                                            ? null
                                            : <>
                                                <button
                                                    type="button"
                                                    onClick={() => editRole(item.id)}
                                                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                                                    Edit
                                                </button>
                                                {item.status == 1 && canDisable && <button
                                                    type="button"
                                                    onClick={() => deleteRole(item.id)}
                                                    className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-200">
                                                    Disable
                                                </button>}
                                                {item.status == 0 && canActivate && <button
                                                    type="button"
                                                    onClick={() => enableRole(item.id)}
                                                    className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring focus:ring-green-200">
                                                    Enable
                                                </button>}
                                                {canDelete && <button
                                                    type="button"
                                                    onClick={() => destroyRole(item.id)}
                                                    className="px-3 py-1 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 focus:outline-none focus:ring focus:ring-red-300">
                                                    Delete
                                                </button>}
                                            </>}
                                    </div>
                                </td>
                            </tr>);
                        })}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}

export default List;
